import React, { useEffect, useMemo, useRef, useState } from "react";
import { money, moneyC, monthShort, seededSeries, titleCase } from "./format.js";
import { bizPath, cityPath, countyPath, homePath, explorePath, rankingsPath, aboutPath, mapPath, citiesPath, pricingPath, SITE_NAME } from "./slug.js";

/* ---------------------------------------------------------------- SEO links
 * Real <a href> so crawlers can discover the long tail; onClick keeps the SPA
 * navigation (unless a modifier/middle-click wants a new tab). stopPropagation
 * so a link inside a clickable row doesn't fire the row handler too. */
const modifiedClick = (e) => e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1;
export function BizLink({ b, onOpen, className, style, children }) {
  return (
    <a href={bizPath(b)} className={className} style={style}
      onClick={(e) => { if (modifiedClick(e)) return; e.preventDefault(); e.stopPropagation(); onOpen(b); }}>
      {children ?? titleCase(b.name)}
    </a>
  );
}
export function PlaceLink({ href, onNav, className, style, children }) {
  return (
    <a href={href} className={className} style={style}
      onClick={(e) => { if (modifiedClick(e)) return; e.preventDefault(); e.stopPropagation(); onNav(); }}>
      {children}
    </a>
  );
}
export const cityHref = cityPath;
export const countyHref = countyPath;

/* Site footer: crawlable internal links + source attribution (E-E-A-T). */
export function SiteFooter({ onNav }) {
  const link = (href, label, view) => (
    <PlaceLink className="foot-link" href={href} onNav={() => onNav(view)}>{label}</PlaceLink>
  );
  const year = 2026;
  return (
    <footer className="site-footer">
      <div className="foot-inner">
        <div className="foot-col">
          <div className="foot-brand">{SITE_NAME}</div>
          <p className="foot-note">Reported mixed-beverage (bar) sales for every tracked venue, city, and county in Texas. Updated monthly.</p>
        </div>
        <div className="foot-col">
          <div className="foot-h">Explore</div>
          {link(homePath(), "Overview", "overview")}
          {link(mapPath(), "Sales map", "map")}
          {link(citiesPath(), "Cities", "cities")}
          {link(explorePath(), "Explore businesses", "explore")}
          {link(rankingsPath(), "Rankings", "rankings")}
        </div>
        <div className="foot-col">
          <div className="foot-h">About</div>
          {link(aboutPath(), "About & methodology", "about")}
          {link(pricingPath(), "Pricing", "pricing")}
        </div>
        <div className="foot-col">
          <div className="foot-h">Data sources</div>
          <a className="foot-link" href="https://data.texas.gov/dataset/Mixed-Beverage-Gross-Receipts/naix-2893" target="_blank" rel="noopener noreferrer">Texas Comptroller</a>
          <a className="foot-link" href="https://www.tabc.texas.gov/" target="_blank" rel="noopener noreferrer">TABC</a>
        </div>
      </div>
      <div className="foot-legal">
        © {year} {SITE_NAME}. Public data from the Texas Comptroller mixed-beverage gross receipts dataset;
        not affiliated with the Comptroller or TABC.
      </div>
    </footer>
  );
}

/* --------------------------------------------------------------- Category
 * Each category gets its own accent, kept clear of the receipt-mix hues
 * (liquor green, wine mauve, beer gold, cover steel-blue) so pills never
 * clash with the sales-mix legend.                                          */
const CATEGORY_COLORS = {
  "Bar": "#4338CA",          // indigo
  "Restaurant": "#C2410C",   // burnt orange
  "Lounge": "#7C3AED",       // violet
  "Nightclub": "#C026D3",    // magenta
  "Brewery": "#0D9488",      // teal
  "Wine Bar": "#9D174D",     // wine-rose
  "Hotel": "#2563EB",        // blue
  "Live Music": "#0891B2",   // cyan
  "Private Club": "#475569", // slate
};
export function catColor(cat) { return CATEGORY_COLORS[cat] || "#4338CA"; }

/* Colored category pill (per-category hue, soft tinted background). */
export function CatTag({ cat, className = "" }) {
  const c = catColor(cat);
  return (
    <span className={`cat-tag ${className}`} style={{ color: c, borderColor: `${c}66`, background: `${c}14` }}>
      {cat}
    </span>
  );
}

/* ------------------------------------------------------------------- Delta
 * Never color-only: triangle + signed number, per handoff a11y spec.       */
export function Delta({ v, title }) {
  if (v == null) return <span className="delta flat" title="No prior-month filing to compare">–</span>;
  const dir = v > 0.05 ? "up" : v < -0.05 ? "down" : "flat";
  const tri = dir === "up" ? "▲" : dir === "down" ? "▼" : "–";
  const num = dir === "down" ? `−${Math.abs(v).toFixed(1)}%` : `${dir === "up" ? "+" : ""}${Math.abs(v).toFixed(1)}%`;
  return (
    <span className={`delta ${dir}`} title={title}>
      <span className="tri" aria-hidden="true">{tri}</span> {num}
    </span>
  );
}

/* --------------------------------------------------------------- Sparkline
 * Small decorative polyline (metric strip, watchlist). aria-hidden.        */
export function Spark({ series, w = 64, h = 26, color }) {
  const pts = useMemo(() => {
    if (!series?.length) return "";
    const min = Math.min(...series), max = Math.max(...series);
    const span = max - min || 1;
    return series.map((v, i) => {
      const x = (i / (series.length - 1)) * (w - 2) + 1;
      const y = h - 3 - ((v - min) / span) * (h - 6);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ");
  }, [series, w, h]);
  const stroke = color || (series[series.length - 1] >= series[0] ? "var(--pos)" : "var(--neg)");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true" style={{ flex: "none" }}>
      <polyline points={pts} fill="none" stroke={stroke} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

/** Seeded decorative sparkline ending at the actual current value. */
export function SeedSpark({ id, value, chg, w, h }) {
  const series = useMemo(
    () => seededSeries(`${id}:${value}`, value, 10, Math.max(0.02, Math.min(0.1, Math.abs(chg || 3) / 60))),
    [id, value, chg]
  );
  return <Spark series={series} w={w} h={h} color={(chg ?? 0) < 0 ? "var(--neg)" : "var(--pos)"} />;
}

/* ------------------------------------------------------------- RankedBars
 * Overview "Top Businesses by Total Sales". Each bar is a button; hover /
 * focus shows an in-place dark tooltip; click opens the profile.           */
const RAMP = ["#0F6549", "#1A7C5E", "#2A8C6C", "#3E9A7C", "#54A88C", "#6AB59C", "#80C0AC", "#97CCBD", "#ADD8CD", "#C2E2DA"];

export function RankedBars({ items, onOpen }) {
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...items.map((b) => b.total));
  return (
    <div>
      {items.map((b, i) => (
        <a
          key={b.id}
          href={bizPath(b)}
          className="ranked-row"
          aria-label={`${titleCase(b.name)}, ${moneyC(b.total)}, open profile`}
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(null)}
          onFocus={() => setHover(i)}
          onBlur={() => setHover(null)}
          onClick={(e) => { if (modifiedClick(e)) return; e.preventDefault(); onOpen(b); }}
        >
          <span className="ranked-rank">{String(i + 1).padStart(2, "0")}</span>
          <span className="ranked-name" title={titleCase(b.name)}>{titleCase(b.name)}</span>
          <span className="ranked-track">
            <span
              className="ranked-bar"
              style={{ width: `${(b.total / max) * 100}%`, background: hover === i ? "#0A5038" : RAMP[Math.min(i, RAMP.length - 1)] }}
            />
          </span>
          <span className="ranked-val">{money(b.total)}</span>
          {hover === i && (
            <span className="chart-tip" style={{ left: "34%", top: "-58px" }}>
              <b>{titleCase(b.name)}</b><br />
              <span className="dim">{titleCase(b.city)} · {b.county}</span><br />
              {moneyC(b.total)} <Delta v={b.chg} />
            </span>
          )}
        </a>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------- TrendChart
 * Line/area chart. Hover readout is drawn INSIDE the fixed SVG (dark pill +
 * dashed guide + dot) so there is no layout shift - per handoff, preserve. */
export function TrendChart({ data, height = 250, big = false }) {
  const [hover, setHover] = useState(null);
  const wrapRef = useRef(null);
  /* Render the SVG viewBox at the container's real pixel width so 1 unit = 1px:
   * chart text (axes, month labels, hover readout) then stays legible at any
   * width instead of shrinking with the SVG on a phone. */
  const [cssW, setCssW] = useState(700);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setCssW(Math.max(el.clientWidth || 700, 240));
    update();
    let ro;
    if (typeof ResizeObserver !== "undefined") { ro = new ResizeObserver(update); ro.observe(el); }
    else window.addEventListener("resize", update);
    return () => { if (ro) ro.disconnect(); else window.removeEventListener("resize", update); };
  }, []);

  const narrow = cssW < 460;
  const W = Math.round(cssW), H = height;
  const PAD = { t: 18, r: 14, b: 28, l: narrow ? 46 : 52 };
  const AX = narrow ? 13 : 11;             // axis / month label px
  const TIP_L = narrow ? 13 : 11;          // tooltip month px
  const TIP_V = narrow ? 15 : 12.5;        // tooltip value px

  const { pts, ticks } = useMemo(() => {
    const clean = (data || []).filter((d) => Number.isFinite(d.v));
    const vals = clean.map((d) => d.v);
    const min = vals.length ? Math.min(...vals) : 0;
    const max = vals.length ? Math.max(...vals) : 1;
    const range = max - min;
    /* Flat series (all months equal, or a single period) would give hi === lo
     * and divide-by-zero NaN coordinates - pad the domain so it always renders. */
    const lo = range > 0 ? Math.max(0, min - range * 0.15) : 0;
    const hi = range > 0 ? max + range * 0.12 : (max > 0 ? max * 1.15 : 1);
    const span = hi - lo || 1;
    const iw = W - PAD.l - PAD.r, ih = H - PAD.t - PAD.b;
    const pts = clean.map((d, i) => ({
      ...d,
      x: PAD.l + (clean.length === 1 ? iw / 2 : (i / (clean.length - 1)) * iw),
      y: PAD.t + ih - ((d.v - lo) / span) * ih,
    }));
    const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => ({ v: lo + span * f, y: PAD.t + ih - f * ih }));
    return { pts, ticks };
  }, [data, H, W, PAD.l, PAD.r]);

  if (!pts.length) {
    return <div className="empty-panel"><div className="big">No trend data</div>Not enough reporting periods.</div>;
  }

  const line = pts.length === 1
    ? `M${PAD.l},${pts[0].y.toFixed(1)} L${W - PAD.r},${pts[0].y.toFixed(1)}`
    : pts.map((p, i) => `${i ? "L" : "M"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const area = `${line} L${(pts[pts.length - 1].x).toFixed(1)},${H - PAD.b} L${pts[0].x.toFixed(1)},${H - PAD.b} Z`;
  const hp = hover != null ? pts[hover] : null;
  const tipW = narrow ? 150 : 128, tipH = narrow ? 42 : 34;
  const tipX = hp ? Math.min(Math.max(hp.x - tipW / 2, 2), W - tipW - 2) : 0;
  const tipY = hp ? Math.max(hp.y - tipH - 12, 2) : 0;
  const hideOdd = pts.length > 14 || (narrow && pts.length > 7);

  return (
    <div ref={wrapRef} style={{ width: "100%" }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ display: "block" }} role="img" aria-label="Monthly sales trend">
        {ticks.map((t) => (
          <g key={t.y}>
            <line x1={PAD.l} x2={W - PAD.r} y1={t.y} y2={t.y} stroke="var(--hairline)" />
            <text x={PAD.l - 8} y={t.y + AX * 0.34} textAnchor="end" fontSize={AX} fill="var(--faint-2)" fontFamily="var(--font-mono)">{money(t.v)}</text>
          </g>
        ))}
        <path d={area} fill="#167C5B" opacity="0.14" />
        <path d={line} fill="none" stroke="#167C5B" strokeWidth={big ? 2.4 : 2} strokeLinejoin="round" strokeLinecap="round" />
        {pts.map((p, i) => (
          <text key={p.m} x={p.x} y={H - 8} textAnchor="middle" fontSize={AX} fill="var(--faint-2)" fontFamily="var(--font-mono)"
            style={{ display: hideOdd && i % 2 ? "none" : undefined }}>
            {monthShort(p.m)}
          </text>
        ))}
        {hp && (
          <g pointerEvents="none">
            <line x1={hp.x} x2={hp.x} y1={PAD.t} y2={H - PAD.b} stroke="var(--faint-3)" strokeDasharray="3 4" />
            <circle cx={hp.x} cy={hp.y} r={narrow ? 5 : 4} fill="#167C5B" stroke="#FCFCFA" strokeWidth="2" />
            <rect x={tipX} y={tipY} width={tipW} height={tipH} rx="6" fill="#17181B" />
            <text x={tipX + tipW / 2} y={tipY + tipH * 0.40} textAnchor="middle" fontSize={TIP_L} fill="#B9BAB6" fontFamily="var(--font-mono)">
              {monthShort(hp.m)} {hp.m.slice(0, 4)}
            </text>
            <text x={tipX + tipW / 2} y={tipY + tipH * 0.78} textAnchor="middle" fontSize={TIP_V} fontWeight="700" fill="#F1F1EE">
              {moneyC(hp.v)}
            </text>
          </g>
        )}
        {/* invisible per-month hover columns (tap/hover) */}
        {pts.map((p, i) => {
          const x0 = i === 0 ? PAD.l : (pts[i - 1].x + p.x) / 2;
          const x1 = i === pts.length - 1 ? W - PAD.r : (p.x + pts[i + 1].x) / 2;
          return (
            <rect key={`h${p.m}`} x={x0} y={PAD.t} width={Math.max(x1 - x0, 0)} height={H - PAD.t - PAD.b}
              fill="transparent" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
              onTouchStart={() => setHover(i)} />
          );
        })}
      </svg>
    </div>
  );
}

/* --------------------------------------------------------------- Sales mix */
export const CAT_COLORS = { l: "var(--c-liquor)", w: "var(--c-wine)", b: "var(--c-beer)", c: "var(--c-cover)" };
const CAT_LABELS = { l: "Liquor", w: "Wine", b: "Beer", c: "Cover charge" };

export function MixStack({ mix, total }) {
  const keys = ["l", "w", "b", "c"];
  return (
    <>
      <div className="mix-stack" role="img" aria-label={keys.map((k) => `${CAT_LABELS[k]} ${Math.round((mix?.[k] || 0) * 100)}%`).join(", ")}>
        {keys.map((k) => <i key={k} style={{ width: `${(mix?.[k] || 0) * 100}%`, background: CAT_COLORS[k] }} />)}
      </div>
      <div className="mix-legend">
        {keys.map((k) => (
          <div className="mix-leg-row" key={k}>
            <span className="sw" style={{ background: CAT_COLORS[k] }} />
            <span className="lab">{CAT_LABELS[k]}</span>
            <span className="dollars">{money((mix?.[k] || 0) * (total || 0))}</span>
            <span className="share">{Math.round((mix?.[k] || 0) * 100)}%</span>
          </div>
        ))}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------- icons */
const PATHS = {
  search: "M15.5 14h-.8l-.3-.3a6.5 6.5 0 10-.7.7l.3.3v.8l5 5 1.5-1.5-5-5zm-6 0a4.5 4.5 0 110-9 4.5 4.5 0 010 9z",
  export: "M12 3v10m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2",
  menu: "M3 6h18M3 12h18M3 18h18",
  chevD: "M2 4l4 4 4-4",
};
export function Icon({ name, stroke = false }) {
  return stroke ? (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="butt" strokeLinejoin="miter" aria-hidden="true" style={{ width: "100%", height: "100%" }}>
      <path d={PATHS[name]} />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ width: "100%", height: "100%" }}>
      <path d={PATHS[name]} />
    </svg>
  );
}
export function ChevronDown() {
  return (
    <svg className="chev" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="M2.5 4.5L6 8l3.5-3.5" />
    </svg>
  );
}

/* ----------------------------------------------------------------- states */
export function Skel({ w = "100%", h = 12, style }) {
  return <span className="skel" style={{ width: w, height: h, ...style }} />;
}

export function MetricSkeleton() {
  return (
    <div className="metric-strip" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <div className="metric-cell" key={i}>
          <Skel w="55%" h={10} style={{ marginBottom: 14 }} />
          <Skel w="65%" h={26} style={{ marginBottom: 10 }} />
          <Skel w="45%" h={11} />
        </div>
      ))}
    </div>
  );
}

export function TableSkeleton({ rows = 8 }) {
  return (
    <div className="panel panel-pad" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} style={{ display: "flex", gap: 16, padding: "11px 0", alignItems: "center" }}>
          <Skel w={24} h={11} /><Skel w="30%" h={13} /><Skel w="12%" h={11} /><Skel w="10%" h={11} />
          <span style={{ flex: 1 }} /><Skel w={90} h={13} /><Skel w={52} h={12} />
        </div>
      ))}
    </div>
  );
}

export function Empty({ title = "No results", children, cta, onCta }) {
  return (
    <div className="empty-panel">
      <div className="big">{title}</div>
      <div>{children}</div>
      {cta && <div className="cta"><button className="btn primary" onClick={onCta}>{cta}</button></div>}
    </div>
  );
}

export function ErrorPanel({ message, onRetry }) {
  return (
    <div className="alert-crit" role="alert">
      <div className="msg"><b>Couldn’t load reports.</b> {message || "The data service is unreachable."}</div>
      {onRetry && <button className="btn" onClick={onRetry}>Retry</button>}
    </div>
  );
}
